import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ClipboardText, MagnifyingGlass, WarningCircle } from "@phosphor-icons/react";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

/* Pricing-safe, Excel-style material receipt board for the Store team — every active PO's
   lines flattened into one sheet, updated one row at a time. Admin controls (Company Settings
   → Store Access) whether vendor name / rate / value are shown here at all. */
export default function StoreReceipts() {
  const [rows, setRows] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [qty, setQty] = useState({});
  const [saving, setSaving] = useState({});
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);

  const load = () => {
    api.get("/purchase-orders/store-view")
      .then((r) => { setRows(r.data.pos || []); setShowPricing(!!r.data.show_pricing); setError(null); })
      .catch((err) => setError(apiError(err.response?.data?.detail) || "Could not load Material Receipts"));
  };
  useEffect(load, []);

  const flat = (rows || []).flatMap((p) =>
    (p.items || []).filter((it) => it.remaining > 0 || true).map((it) => ({ ...it, po: p })));
  const q = search.toLowerCase();
  const filtered = flat.filter((r) => !q
    || (r.po.po_no || "").toLowerCase().includes(q)
    || (r.item || "").toLowerCase().includes(q)
    || (r.po.vendor_name || "").toLowerCase().includes(q));

  const receive = async (row) => {
    const key = `${row.po.id}_${row.id}`;
    const val = Number(qty[key]);
    if (!val || val <= 0) return toast.error("Enter a quantity greater than 0");
    if (val > row.remaining + 1e-9) return toast.error(`Only ${row.remaining} remaining for this line`);
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.post(`/purchase-orders/${row.po.id}/grn`, {
        entries: [{ item_id: row.id, qty_received: val }],
        note: "Recorded via Material Receipts (Store)",
        grn_date: new Date().toISOString().slice(0, 10),
      });
      toast.success(`${val} recorded for ${row.item}`);
      setQty((q2) => ({ ...q2, [key]: "" }));
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving((s) => ({ ...s, [key]: false })); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] animate-fade-in">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-emerald-600 font-bold mb-1">Procurement</div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-zinc-50 flex items-center gap-2">
            <ClipboardText size={26} className="text-emerald-600" weight="bold" /> Material Receipts
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Update received / pending quantity for every PO line, Excel-style. {!showPricing && "Pricing and vendor value are hidden on this view."}</p>
        </div>
        <div className="relative w-full max-w-xs">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search PO no, item, vendor…"
            className="bg-zinc-950 border-zinc-800 rounded-sm text-zinc-100 pl-9 focus:border-emerald-600 focus-visible:ring-emerald-600" data-testid="store-search" />
        </div>
      </div>

      {error ? (
        <div className="border border-red-500/30 bg-red-500/5 rounded-sm p-8 text-center text-sm text-red-400 flex flex-col items-center gap-2" data-testid="store-receipts-error">
          <WarningCircle size={22} /> {error}
        </div>
      ) : (
        <div className="border border-zinc-800 bg-zinc-900/30 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-left text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3 font-bold">PO No</th>
                {showPricing && <th className="px-4 py-3 font-bold">Vendor</th>}
                <th className="px-4 py-3 font-bold">Item</th>
                <th className="px-4 py-3 font-bold text-right">Ordered</th>
                <th className="px-4 py-3 font-bold text-right">Received</th>
                <th className="px-4 py-3 font-bold text-right">Remaining</th>
                {showPricing && <th className="px-4 py-3 font-bold text-right">Rate</th>}
                <th className="px-4 py-3 font-bold text-right">Receive now</th>
                <th className="px-4 py-3 font-bold text-center">Save</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {filtered.map((row) => {
                const key = `${row.po.id}_${row.id}`;
                return (
                  <tr key={key} data-testid={`store-row-${key}`} className={row.remaining <= 0 ? "opacity-50" : ""}>
                    <td className="px-4 py-2.5 font-mono text-emerald-600 font-bold">{row.po.po_no}</td>
                    {showPricing && <td className="px-4 py-2.5 text-zinc-300">{row.po.vendor_name}</td>}
                    <td className="px-4 py-2.5 text-zinc-100">{row.item}
                      {row.specification && <div className="text-[10px] text-zinc-500 font-mono">{row.specification}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-zinc-300">{row.qty}</td>
                    <td className={`px-4 py-2.5 text-right font-mono ${row.received > 0 ? "text-emerald-400" : "text-zinc-500"}`}>{row.received}</td>
                    <td className={`px-4 py-2.5 text-right font-mono ${row.remaining > 0 ? "text-amber-400" : "text-zinc-500"}`}>{row.remaining}</td>
                    {showPricing && <td className="px-4 py-2.5 text-right font-mono text-zinc-400">₹ {inr(row.rate)}</td>}
                    <td className="px-4 py-2.5 text-right">
                      <Input type="number" min="0" max={row.remaining} step="any" disabled={row.remaining <= 0}
                        value={qty[key] ?? ""} placeholder={row.remaining > 0 ? "0" : "—"}
                        onChange={(e) => setQty((q2) => ({ ...q2, [key]: e.target.value }))}
                        className="w-24 ml-auto h-8 bg-zinc-950 border-zinc-800 rounded-sm text-right font-mono text-zinc-100 focus:border-emerald-600 focus-visible:ring-emerald-600"
                        data-testid={`store-qty-${key}`} />
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <Button size="sm" disabled={row.remaining <= 0 || saving[key]} onClick={() => receive(row)} data-testid={`store-save-${key}`}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs h-7">
                        {saving[key] ? "…" : "Save"}
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {rows && filtered.length === 0 && (
                <tr><td colSpan={showPricing ? 9 : 7} className="px-4 py-10 text-center text-sm text-zinc-500" data-testid="store-empty">
                  {rows.length === 0 ? "No active Purchase Order lines pending receipt right now." : "No lines match your search."}
                </td></tr>
              )}
              {!rows && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-zinc-500">Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
